import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router";
import { useAuth, usePermission } from "@ncp/api-client";
import { ConsoleLayout, RootLayout } from "./layouts";
import { LoginPage } from "../pages/LoginPage";
import { OverviewPage } from "../pages/OverviewPage";
import { WorkflowStudioPage } from "../pages/WorkflowStudioPage";
import { IntegrationsPage } from "../pages/IntegrationsPage";
import { GovernancePage } from "../pages/GovernancePage";
import { AuditPage } from "../pages/AuditPage";
import { FeedbackPage } from "../pages/FeedbackPage";
import { ReleasesPage } from "../pages/ReleasesPage";
import { PackagesPage } from "../pages/PackagesPage";
import { EnvironmentsPage } from "../pages/EnvironmentsPage";
import { SettingsPage } from "../pages/SettingsPage";
import { IdentityPage } from "../pages/IdentityPage";
import { FormsPage } from "../pages/FormsPage";
import { FormBuilderPage } from "../pages/FormBuilderPage";
import { ServicesPage } from "../pages/ServicesPage";
import { ServiceDesignerPage } from "../pages/ServiceDesignerPage";
import { RulesPage } from "../pages/RulesPage";
import { RuleDesignerPage } from "../pages/RuleDesignerPage";
import { ToolsPage } from "../pages/ToolsPage";
import { ToolDesignerPage } from "../pages/ToolDesignerPage";
import { RecordTypesPage } from "../pages/RecordTypesPage";
import { RecordTypeDesignerPage } from "../pages/RecordTypeDesignerPage";
import { ReportsPage } from "../pages/ReportsPage";
import { ReportDesignerPage } from "../pages/ReportDesignerPage";
import { ScreensPage } from "../pages/ScreensPage";
import { ScreenDesignerPage } from "../pages/ScreenDesignerPage";
import { WorkspacesPage } from "../pages/WorkspacesPage";
import { WorkspacePage } from "../pages/WorkspacePage";
import { PagesPage } from "../pages/PagesPage";
import { PageStudioPage } from "../pages/PageStudioPage";
import { SolutionsPage } from "../pages/SolutionsPage";
import { SolutionPage } from "../pages/SolutionPage";
import { AssistantsPage } from "../pages/AssistantsPage";
import { AssistantBuilderPage } from "../pages/AssistantBuilderPage";
import { KnowledgePage } from "../pages/KnowledgePage";
import { AgentsPage } from "../pages/AgentsPage";
import { AgentBuilderPage } from "../pages/AgentBuilderPage";
import { AutomationsPage } from "../pages/AutomationsPage";
import { AutomationEditorPage } from "../pages/AutomationEditorPage";
import { ConnectorBuilderPage } from "../pages/ConnectorBuilderPage";
import { ForbiddenPage } from "../pages/ForbiddenPage";
import { WorkflowBuilderPage } from "../features/builder/BuilderPage";

/** Staff with at least one platform permission may enter the console; everyone else is sent to sign in. */
function RequireConsoleAccess() {
  const { isAuthenticated, user } = useAuth();
  const { permissions } = usePermission();
  const { pathname, search, hash } = useLocation();
  if (!isAuthenticated || user?.kind !== "Staff") return <Navigate to="/login" replace state={{ from: `${pathname}${search}${hash}` }} />;
  // A classifier of reported defects (condition 1.7) enters for the reported issues alone.
  const hasPlatformAccess = permissions.some((p) => p === "*" || p.startsWith("platform.") || p === "pages.author" || p === "issues.classify");
  if (!hasPlatformAccess) return <ForbiddenPage />;
  return <Outlet />;
}

/** Page authors without the platform overview land in Page Studio. */
function Landing() {
  const { can } = usePermission();
  if (!can("platform.overview.read") && can("pages.author")) return <Navigate to="/pages" replace />;
  // An officer who only classifies reported defects (the Head of Complaints) lands on the reported issues (condition 1.7).
  if (!can("platform.overview.read") && can("issues.classify")) return <Navigate to="/feedback" replace />;
  return <OverviewPage />;
}

/** Guards a single route behind a permission (or any one of several); the page itself gates write actions. */
export function RequirePermission({ permission }: { permission: string | string[] }) {
  const { canAny } = usePermission();
  const any = Array.isArray(permission) ? permission : [permission];
  if (!canAny(...any)) return <ForbiddenPage permission={any.join(" · ")} />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      {
        element: <RequireConsoleAccess />,
        children: [
          // The Workflow Builder is full-screen: it brings its own top bar instead of the console chrome.
          { element: <RequirePermission permission="platform.workflows.read" />, children: [{ path: "/workflows/:key", element: <WorkflowBuilderPage /> }] },
          {
            element: <ConsoleLayout />,
            children: [
              { index: true, element: <Landing /> },
              { element: <RequirePermission permission="platform.services.read" />, children: [{ path: "services", element: <ServicesPage /> }, { path: "services/:key", element: <ServiceDesignerPage /> }] },
              { element: <RequirePermission permission="platform.rules.read" />, children: [{ path: "rules", element: <RulesPage /> }, { path: "rules/:key", element: <RuleDesignerPage /> }] },
              { element: <RequirePermission permission="platform.tools.read" />, children: [{ path: "tools", element: <ToolsPage /> }, { path: "tools/:key", element: <ToolDesignerPage /> }] },
              { element: <RequirePermission permission="platform.recordtypes.read" />, children: [{ path: "data", element: <RecordTypesPage /> }, { path: "data/:key", element: <RecordTypeDesignerPage /> }] },
              { element: <RequirePermission permission="platform.reports.read" />, children: [{ path: "reports", element: <ReportsPage /> }, { path: "reports/:key", element: <ReportDesignerPage /> }] },
              { element: <RequirePermission permission="platform.screens.read" />, children: [{ path: "screens", element: <ScreensPage /> }, { path: "screens/:key", element: <ScreenDesignerPage /> }] },
              { element: <RequirePermission permission="platform.forms.read" />, children: [{ path: "forms", element: <FormsPage /> }, { path: "forms/:key", element: <FormBuilderPage /> }] },
              { element: <RequirePermission permission="platform.workflows.read" />, children: [{ path: "workflows", element: <WorkflowStudioPage /> }] },
              { element: <RequirePermission permission="platform.automations.read" />, children: [{ path: "automations", element: <AutomationsPage /> }, { path: "automations/:key", element: <AutomationEditorPage /> }] },
              { element: <RequirePermission permission="pages.author" />, children: [{ path: "pages", element: <PagesPage /> }, { path: "pages/:key", element: <PageStudioPage /> }] },
              { element: <RequirePermission permission="platform.workspaces.read" />, children: [{ path: "workspaces", element: <WorkspacesPage /> }, { path: "workspaces/:key", element: <WorkspacePage /> }, { path: "solutions", element: <SolutionsPage /> }, { path: "solutions/:key", element: <SolutionPage /> }] },
              { element: <RequirePermission permission="platform.assistants.read" />, children: [{ path: "assistants", element: <AssistantsPage /> }, { path: "assistants/:key", element: <AssistantBuilderPage /> }] },
              { element: <RequirePermission permission="platform.knowledge.read" />, children: [{ path: "knowledge", element: <KnowledgePage /> }, { path: "knowledge/:key", element: <KnowledgePage /> }] },
              { element: <RequirePermission permission="platform.agents.read" />, children: [{ path: "agents", element: <AgentsPage /> }, { path: "agents/:key", element: <AgentBuilderPage /> }] },
              { element: <RequirePermission permission="platform.integrations.read" />, children: [{ path: "integrations", element: <IntegrationsPage /> }, { path: "integrations/:tab", element: <IntegrationsPage /> }, { path: "integrations/connectors/:key", element: <ConnectorBuilderPage /> }] },
              { path: "identity", element: <IdentityPage /> },
              { path: "identity/:tab", element: <IdentityPage /> },
              { element: <RequirePermission permission="platform.governance.read" />, children: [{ path: "governance", element: <GovernancePage /> }] },
              { element: <RequirePermission permission="platform.audit.read" />, children: [{ path: "audit", element: <AuditPage /> }] },
              // The reported issues open to the audit readers and to every role that classifies a defect, without the audit trail (condition 1.7).
              { element: <RequirePermission permission={["platform.audit.read", "issues.classify"]} />, children: [{ path: "feedback", element: <FeedbackPage /> }] },
              { element: <RequirePermission permission="platform.releases.read" />, children: [{ path: "releases", element: <ReleasesPage /> }, { path: "releases/:id", element: <ReleasesPage /> }] },
              { element: <RequirePermission permission="platform.packages.read" />, children: [{ path: "packages", element: <PackagesPage /> }, { path: "packages/:id", element: <PackagesPage /> }] },
              { element: <RequirePermission permission="platform.environments.read" />, children: [{ path: "environments", element: <EnvironmentsPage /> }] },
              { path: "settings", element: <SettingsPage /> },
              { path: "settings/:tab", element: <SettingsPage /> },
              // Legacy back-office paths kept as redirects so bookmarks survive the move.
              { path: "workflow-studio", element: <Navigate to="/workflows" replace /> },
              { path: "ai-governance", element: <Navigate to="/governance" replace /> },
              { path: "*", element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
], { basename: import.meta.env.BASE_URL });
